import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { setAuth } from "./Redux/authSlice";
import axios from "axios";
import {
  FaUserGraduate,
  FaExclamationTriangle,
  FaMoneyBillAlt,
  FaChartLine,
  FaBell,
  FaCheckCircle,
  FaTimesCircle,
  FaHome,
  FaUser,
  FaSignOutAlt,
  FaChevronRight,
  FaCalendarAlt,
  FaPhone,
  FaMapMarkerAlt
} from "react-icons/fa";

// Components
import DisciplineCard from "./DisciplineCard";
import FeeCard from "./FeeCard";

// Types
import { Student, Discipline, Fee, BlogPost, Attendance } from "./types";
import ParentResults from "./Parent/ParentResults";

const ParentDashboard: React.FC = () => {
  const dispatch = useDispatch();

  const [students, setStudents] = useState<Student[]>([]);
  const [disciplines, setDisciplines] = useState<Discipline[]>([]);
  const [fees, setFees] = useState<Fee[]>([]);
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [attendance, setAttendance] = useState<Attendance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'home' | 'students' | 'fees' | 'profile' | 'results' | 'notifications' | 'attendance'>('home'); // Add results

  const handleSignOut = () => {
    // Clear the token cookie
    document.cookie = 'token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    dispatch(setAuth(false));
    // Redirect to login page
    window.location.href = '/';
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        const token = getCookie("token");
        if (!token) {
          dispatch(setAuth(false));
          return;
        }

        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        const studentResponse = await axios.get(
          "/Branches/parents/"
        );
        if (studentResponse.data.length > 0) {
          setStudents(studentResponse.data[0].student_info);
          const feesResponse = await axios.get(
            "/fees/fees/"
          );
          setFees(feesResponse.data.results || []);
          const disciplineResponse = await axios.get(
            "/Branches/disciplines/"
          );
          setDisciplines(disciplineResponse.data);

          const postsResponse = await axios.get(
            "/Branches/posts/"
          );
          setPosts(postsResponse.data);

          const attendanceResponse = await axios.get(
            "/Branches/attendance/"
          );
          setAttendance(attendanceResponse.data.attendance_records || []);
        } else {
          setStudents([]);
        }
        dispatch(setAuth(true));
      } catch (error) {
        console.error("Error fetching data:", error);
        dispatch(setAuth(false));
        setError("Waa la waayay xogta. Fadlan isku day mar kale gadaal.");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [dispatch]);

  const getCookie = (name: string) => {
    const cookieName = name + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    return (
      decodedCookie
        .split(";")
        .map((c) => c.trim())
        .find((c) => c.startsWith(cookieName))?.substring(cookieName.length) ||
      ""
    );
  };

  // Calculate statistics
  // Calculate statistics
  const currentMonthName = new Date().toLocaleString('en-US', { month: 'long' });
  const currentMonthIndex = new Date().getMonth();
  const currentYear = new Date().getFullYear();

  const paidFees = fees.filter(fee => parseFloat(fee.balance) <= 0).length;
  const unpaidFees = fees.filter(fee => parseFloat(fee.balance) > 0).length;
  const totalBalance = fees.reduce((sum, fee) => sum + (parseFloat(fee.balance) || 0), 0);

  // Current Month Calculations - Robust Logic
  const currentMonthFees = fees.filter(fee => {
    // 1. Check if month string matches (case-insensitive, trimmed)
    if (fee.month && fee.month.trim().toLowerCase() === currentMonthName.toLowerCase()) {
      return true;
    }
    // 2. Fallback: Check date_paid if available
    if (fee.date_paid) {
      const paidDate = new Date(fee.date_paid);
      return paidDate.getMonth() === currentMonthIndex && paidDate.getFullYear() === currentYear;
    }
    return false;
  });

  const currentMonthPending = currentMonthFees.filter(fee => parseFloat(fee.balance) > 0).length;
  const currentMonthTotalPending = currentMonthFees.reduce((sum, fee) => sum + (parseFloat(fee.balance) || 0), 0);

  if (loading) {
    return (
      <div className="mobile-app-container">
        <div className="d-flex flex-column justify-content-center align-items-center vh-100 bg-primary">
          <div className="spinner-border text-white mb-3" role="status" style={{ width: '3rem', height: '3rem' }}></div>
          <p className="text-white">Soo Kabanaya...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mobile-app-container">
        <div className="d-flex flex-column justify-content-center align-items-center vh-100 p-4">
          <div className="text-center">
            <div className="bg-danger bg-opacity-10 rounded-circle d-inline-flex p-4 mb-3">
              <FaExclamationTriangle className="text-danger" size={48} />
            </div>
            <h4 className="fw-bold text-dark mb-3">Cillad Xiriirka</h4>
            <p className="text-muted mb-4">{error}</p>
            <button
              className="btn btn-primary btn-lg rounded-pill px-5 shadow"
              onClick={() => window.location.reload()}
            >
              Isku Day Mar Kale
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (students.length === 0) {
    return (
      <div className="mobile-app-container">
        <div className="d-flex flex-column justify-content-center align-items-center vh-100 p-4">
          <div className="text-center">
            <div className="bg-primary bg-opacity-10 rounded-circle d-inline-flex p-4 mb-3">
              <FaUserGraduate className="text-primary" size={48} />
            </div>
            <h4 className="fw-bold text-dark mb-3">Arday Lama Helin</h4>
            <p className="text-muted mb-4">Ma jiraan arday ku xiran ciwaankaaga. Fadlan la xiriir maamulka iskuulka.</p>
            <button className="btn btn-primary btn-lg rounded-pill px-5 shadow">
              La Xiriir Iskuulka
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-app-container">
      {/* Mobile Status Bar */}
      <div className="mobile-status-bar">
        <div className="d-flex justify-content-between align-items-center px-3 py-2">
          <span className="small text-white">Xarunta Waalidka</span>
          <span className="small text-white">{new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
        </div>
      </div>

      {/* Main Content */}
      <div className="mobile-content" style={{ paddingBottom: '80px' }}>
        {/* HOME TAB */}
        {activeTab === 'home' && (
          <div>
            {/* Hero Section */}
            <div className="mobile-hero-section">
              <div className="p-4 text-white">
                <h5 className="mb-2 opacity-90">Soo Dhowow!</h5>
                <h2 className="fw-bold mb-3">Muuraayada Waalidka</h2>
                <p className="mb-0 opacity-90 small">La soco heerka carruurtaada</p>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="px-3 mt-n4">
              <div className="row g-3">
                <div className="col-6">
                  <div className="stat-card shadow-sm">
                    <div className="stat-icon bg-primary bg-opacity-10">
                      <FaUserGraduate className="text-primary" size={24} />
                    </div>
                    <div className="stat-value">{students.length}</div>
                    <div className="stat-label">Carruurtayda</div>
                  </div>
                </div>
                <div className="col-6">
                  <div className="stat-card shadow-sm">
                    <div className="stat-icon bg-danger bg-opacity-10">
                      <FaExclamationTriangle className="text-danger" size={24} />
                    </div>
                    <div className="stat-value">{disciplines.length}</div>
                    <div className="stat-label">Anshaxa</div>
                  </div>
                </div>
                <div className="col-6">
                  <div className="stat-card shadow-sm">
                    <div className="stat-icon bg-success bg-opacity-10">
                      <FaCheckCircle className="text-success" size={24} />
                    </div>
                    <div className="stat-value">{paidFees}</div>
                    <div className="stat-label">La Bixiyay</div>
                  </div>
                </div>
                <div className="col-6">
                  <div className="stat-card shadow-sm">
                    <div className="stat-icon bg-warning bg-opacity-10">
                      <FaMoneyBillAlt className="text-warning" size={24} />
                    </div>
                    <div className="stat-value">ksh {totalBalance.toFixed(0)}</div>
                    <div className="stat-label">Haraaga</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Latest Notifications Preview */}
            {posts.length > 0 && (
              <div className="px-3 mt-4">
                <div className="card border-0 shadow-sm" onClick={() => setActiveTab('notifications')} style={{ cursor: 'pointer' }}>
                  <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
                    <h6 className="fw-bold mb-0 text-primary">
                      <FaBell className="me-2" />
                      Wararka Cusub
                    </h6>
                    <span className="badge bg-danger rounded-pill">{posts.length}</span>
                  </div>
                  <div className="card-body">
                    <div className="d-flex align-items-center mb-2">
                      <span className="fw-bold text-dark text-truncate d-block" style={{ maxWidth: '100%' }}>{posts[0].title}</span>
                    </div>
                    <p className="text-muted small mb-0 text-truncate">{posts[0].content}</p>
                    <small className="text-primary mt-2 d-block">Riix si aad u aragto dhammaan &rarr;</small>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="px-3 mt-4">
              <h6 className="fw-bold mb-3">Falalka Degdega ah</h6>
              <div className="list-group list-group-flush">
                <button
                  className="list-group-item list-group-item-action d-flex align-items-center py-3 border-0 bg-white mb-2 rounded-3 shadow-sm"
                  onClick={() => setActiveTab('students')}
                >
                  <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-3">
                    <FaUserGraduate className="text-primary" size={20} />
                  </div>
                  <div className="flex-grow-1">
                    <div className="fw-semibold">Arag Carruurtayda</div>
                    <small className="text-muted">{students.length} arday</small>
                  </div>
                  <FaChevronRight className="text-muted" />
                </button>

                <button
                  className="list-group-item list-group-item-action d-flex align-items-center py-3 border-0 bg-white mb-2 rounded-3 shadow-sm"
                  onClick={() => setActiveTab('results')}
                >
                  <div className="bg-info bg-opacity-10 rounded-circle p-2 me-3">
                    <FaChartLine className="text-info" size={20} />
                  </div>
                  <div className="flex-grow-1">
                    <div className="fw-semibold">Arag Natiijooyinka</div>
                    <small className="text-muted">Fiiri dhibcaha imtixaanka</small>
                  </div>
                  <FaChevronRight className="text-muted" />
                </button>

                <button
                  className="list-group-item list-group-item-action d-flex align-items-center py-3 border-0 bg-white mb-2 rounded-3 shadow-sm"
                  onClick={() => setActiveTab('fees')}
                >
                  <div className="bg-success bg-opacity-10 rounded-circle p-2 me-3">
                    <FaMoneyBillAlt className="text-success" size={20} />
                  </div>
                  <div className="flex-grow-1">
                    <div className="fw-semibold">Taariikhda Lacagaha</div>
                    <small className="text-muted">{fees.length} diiwaan</small>
                  </div>
                  <FaChevronRight className="text-muted" />
                </button>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="px-3 mt-4 mb-4">
              <h6 className="fw-bold mb-3">Wararkii Ugu Dambeeyay</h6>
              <div className="timeline">
                {disciplines.slice(0, 2).map((discipline, index) => (
                  <div key={discipline.id} className="timeline-item bg-white rounded-3 shadow-sm p-3 mb-2">
                    <div className="d-flex align-items-start gap-2">
                      <div className="bg-danger bg-opacity-10 rounded-circle p-2 flex-shrink-0">
                        <FaExclamationTriangle className="text-danger" size={14} />
                      </div>
                      <div className="flex-grow-1">
                        <div className="fw-semibold small">Diiwaanka Anshaxa</div>
                        <div className="text-muted small">{discipline.case}</div>
                        <div className="text-muted small">{new Date(discipline.date_of_incident).toLocaleDateString()}</div>
                      </div>
                    </div>
                  </div>
                ))}
                {fees.filter(f => parseFloat(f.balance) > 0).slice(0, 1).map((fee, index) => (
                  <div key={index} className="timeline-item bg-white rounded-3 shadow-sm p-3 mb-2">
                    <div className="d-flex align-items-start gap-2">
                      <div className="bg-warning bg-opacity-10 rounded-circle p-2 flex-shrink-0">
                        <FaMoneyBillAlt className="text-warning" size={14} />
                      </div>
                      <div className="flex-grow-1">
                        <div className="fw-semibold small">Lacag Dhiman</div>
                        <div className="text-muted small">{fee.student_name} - ksh {fee.balance}</div>
                        <div className="text-muted small">{fee.month}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* RESULTS TAB */}
        {activeTab === 'results' && (
          <ParentResults students={students} onBack={() => setActiveTab('home')} />
        )}

        {/* STUDENTS TAB */}
        {activeTab === 'students' && (
          <div className="p-3">
            <div className="d-flex align-items-center mb-4">
              <h4 className="fw-bold mb-0">Carruurtayda</h4>
              <span className="badge bg-primary rounded-pill ms-auto">{students.length}</span>
            </div>
            <div className="row g-3">
              {students.map((student) => (
                <div className="col-12" key={student.id}>
                  <div className="card border-0 shadow-sm rounded-3 overflow-hidden">
                    <div className="card-body p-3">
                      <div className="d-flex align-items-center gap-3">
                        {/* Student Image */}
                        <div className="flex-shrink-0">
                          <img
                            src={student.image ? `${student.image}` : 'https://via.placeholder.com/80x80?text=Student'}
                            alt={student.name}
                            className="rounded-circle"
                            style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = 'https://via.placeholder.com/80x80?text=No+Image';
                            }}
                          />
                        </div>
                        {/* Student Details */}
                        <div className="flex-grow-1">
                          <h6 className="fw-bold mb-1">{student.name}</h6>
                          <div className="d-flex gap-2 flex-wrap mb-2">
                            <span className="badge bg-primary rounded-pill">{student.class_name}</span>
                            {student.district && (
                              <span className="badge bg-secondary rounded-pill">{student.district}</span>
                            )}
                          </div>
                          <div className="small">
                            {student.guardian_number && (
                              <div className="text-muted">
                                <FaPhone size={10} className="me-1" />
                                {student.guardian_number}
                              </div>
                            )}
                            {student.address && (
                              <div className="text-muted">
                                <FaMapMarkerAlt size={10} className="me-1" />
                                {student.address}
                              </div>
                            )}
                          </div>
                        </div>
                        {/* View Results Button */}
                        <button
                          className="btn btn-outline-primary rounded-circle p-2"
                          onClick={() => setActiveTab('results')}
                        >
                          <FaChartLine />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Discipline Section for Students */}
            {disciplines.length > 0 && (
              <div className="mt-4">
                <h6 className="fw-bold mb-3 text-danger">
                  <FaExclamationTriangle className="me-2" />
                  Diiwaanada Anshaxa
                </h6>
                <div className="row g-3">
                  {disciplines.map((discipline) => (
                    <div className="col-12" key={discipline.id}>
                      <DisciplineCard discipline={discipline} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* FEES TAB */}
        {activeTab === 'fees' && (
          <div className="p-3">
            <div className="d-flex align-items-center mb-4">
              <h4 className="fw-bold mb-0">Taariikhda Lacagaha</h4>
              <span className="badge bg-success rounded-pill ms-auto">{fees.length}</span>
            </div>

            {/* Fee Summary */}
            <div className="row g-3 mb-4">
              <div className="col-6">
                <div className="card border-0 shadow-sm bg-success bg-opacity-10">
                  <div className="card-body text-center">
                    <FaCheckCircle className="text-success mb-2" size={24} />
                    <h4 className="fw-bold text-success mb-0">{paidFees}</h4>
                    <small className="text-success">La Bixiyay</small>
                  </div>
                </div>
              </div>
              <div className="col-6">
                <div className="card border-0 shadow-sm bg-warning bg-opacity-10">
                  <div className="card-body text-center">
                    <FaTimesCircle className="text-warning mb-2" size={24} />
                    <h4 className="fw-bold text-warning mb-0">{unpaidFees}</h4>
                    <small className="text-warning">Haray</small>
                  </div>
                </div>
              </div>
            </div>
            {/* Current Month Status */}
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-white py-3">
                <h6 className="fw-bold mb-0 text-primary">
                  <FaCalendarAlt className="me-2" />
                  Bisha {currentMonthName}
                </h6>
              </div>
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted">Xaalka:</span>
                  {currentMonthPending > 0 ? (
                    <span className="badge bg-warning text-dark">Lama Dhameystirin</span>
                  ) : currentMonthFees.length > 0 ? (
                    <span className="badge bg-success">Waa La Bixiyay</span>
                  ) : (
                    <span className="badge bg-secondary">Lagama Rabo</span>
                  )}
                </div>
                {currentMonthPending > 0 && (
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="text-muted">Haraaga Bisha:</span>
                    <span className="fw-bold text-danger">ksh {currentMonthTotalPending}</span>
                  </div>
                )}
              </div>
            </div>

            {
              fees.length === 0 ? (
                <div className="text-center py-5">
                  <div className="bg-info bg-opacity-10 rounded-circle d-inline-flex p-4 mb-3">
                    <FaMoneyBillAlt className="text-info" size={32} />
                  </div>
                  <h6 className="fw-bold text-dark">Diiwaan Lacageed Lama Helin</h6>
                  <p className="text-muted small mb-0">Ma jiraan diiwaano lacageed xilligan.</p>
                </div>
              ) : (
                <div className="row g-3">
                  {fees.map((fee, index) => (
                    <div className="col-12" key={`${fee.student_name}-${index}`}>
                      <FeeCard fee={fee} index={index} />
                    </div>
                  ))}
                </div>
              )
            }
          </div>
        )}

        {/* PROFILE TAB */}
        {
          activeTab === 'profile' && (
            <div className="p-3">
              <h4 className="fw-bold mb-4">Ciwaankeyga</h4>

              <div className="card border-0 shadow-sm mb-3">
                <div className="card-body">
                  <div className="text-center mb-4">
                    <div className="bg-primary bg-opacity-10 rounded-circle d-inline-flex p-4 mb-3">
                      <FaUser className="text-primary" size={48} />
                    </div>
                    <h5 className="fw-bold mb-1">Ciwaanka Waalidka</h5>
                    <p className="text-muted small mb-0">Maamul ciwaankaaga iyo settings-ka</p>
                  </div>

                  <div className="list-group list-group-flush">
                    <div className="list-group-item px-0">
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">Carruurta</span>
                        <span className="fw-semibold">{students.length}</span>
                      </div>
                    </div>
                    <div className="list-group-item px-0">
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">Diiwaanada Anshaxa</span>
                        <span className="fw-semibold">{disciplines.length}</span>
                      </div>
                    </div>
                    <div className="list-group-item px-0">
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">Diiwaanada Lacagaha</span>
                        <span className="fw-semibold">{fees.length}</span>
                      </div>
                    </div>
                    <div className="list-group-item px-0">
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">Haraaga Guud</span>
                        <span className="fw-semibold text-warning">ksh {totalBalance.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <button
                className="btn btn-danger w-100 rounded-pill py-3 shadow"
                onClick={handleSignOut}
              >
                <FaSignOutAlt className="me-2" />
                Ka Bax
              </button>
            </div>
          )
        }

        {/* NOTIFICATIONS TAB */}
        {
          activeTab === 'notifications' && (
            <div className="p-3 pb-5 mb-5">
              <h4 className="fw-bold mb-4">Ogeysiisyada</h4>

              {posts.length === 0 ? (
                <div className="text-center py-5">
                  <div className="bg-light rounded-circle d-inline-flex p-4 mb-3">
                    <FaBell className="text-muted" size={32} />
                  </div>
                  <h6 className="text-muted">Ma jiraan ogeysiisyo cusub</h6>
                </div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {posts.map((post) => (
                    <div className="card border-0 shadow-sm" key={post.id}>
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <h6 className="fw-bold mb-0 text-dark">{post.title}</h6>
                          {post.created_at && (
                            <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                              {new Date(post.created_at).toLocaleDateString()}
                            </small>
                          )}
                        </div>
                        <p className="text-muted small mb-0">{post.content}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        }
        {/* ATTENDANCE TAB */}
        {
          activeTab === 'attendance' && (
            <div className="p-3 pb-5 mb-5">
              <h4 className="fw-bold mb-4">Diiwaanka Xaadirinta</h4>

              {attendance.length === 0 ? (
                <div className="text-center py-5">
                  <div className="bg-warning bg-opacity-10 rounded-circle d-inline-flex p-4 mb-3">
                    <FaCalendarAlt className="text-warning" size={32} />
                  </div>
                  <h6 className="fw-bold text-dark">Diiwaan Xaadirin Lama Helin</h6>
                  <p className="text-muted small mb-0">Ma jiraan diiwaano xaadirin xilligan.</p>
                </div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {attendance.map((record) => (
                    <div className="card border-0 shadow-sm" key={record.id}>
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h6 className="fw-bold mb-0 text-dark">{record.student_name}</h6>
                          <span className={`badge ${record.status === 'Present' ? 'bg-success' :
                            record.status === 'Absent' ? 'bg-danger' :
                              record.status === 'Late' ? 'bg-warning text-dark' : 'bg-secondary'
                            }`}>
                            {record.status === 'Present' ? 'Jooga' :
                              record.status === 'Absent' ? 'Ma Joogo' :
                                record.status === 'Late' ? 'Soo Daahay' : record.status}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between text-muted small">
                          <span><FaCalendarAlt className="me-1" /> {record.date}</span>
                          <span>{record.class_name}</span>
                        </div>
                        {record.remarks && (
                          <div className="mt-2 text-muted small border-top pt-2">
                            <strong>Faallo:</strong> {record.remarks}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        }
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="mobile-bottom-nav">
        <button
          className={`nav-btn ${activeTab === 'home' ? 'active' : ''}`}
          onClick={() => setActiveTab('home')}
        >
          <FaHome size={20} />
          <span>Hoyga</span>
        </button>
        <button
          className={`nav-btn ${activeTab === 'students' ? 'active' : ''}`}
          onClick={() => setActiveTab('students')}
        >
          <FaUserGraduate size={20} />
          <span>Carruurta</span>
        </button>
        <button
          className={`nav-btn ${activeTab === 'results' ? 'active' : ''}`}
          onClick={() => setActiveTab('results')}
        >
          <FaChartLine size={20} />
          <span>Natiijooyinka</span>
        </button>
        <button
          className={`nav-btn ${activeTab === 'fees' ? 'active' : ''}`}
          onClick={() => setActiveTab('fees')}
        >
          <FaMoneyBillAlt size={20} />
          <span>Lacagaha</span>
        </button>
        <button
          className={`nav-btn ${activeTab === 'attendance' ? 'active' : ''}`}
          onClick={() => setActiveTab('attendance')}
        >
          <FaCalendarAlt size={20} />
          <span>Xaadirinta</span>
        </button>
        <button
          className={`nav-btn ${activeTab === 'notifications' ? 'active' : ''}`}
          onClick={() => setActiveTab('notifications')}
        >
          <FaBell size={20} />
          <span>Ogeysiis</span>
        </button>
        <button
          className={`nav-btn ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          <FaUser size={20} />
          <span>Ciwaanka</span>
        </button>
      </div >

      <style>{`
        .mobile-app-container {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          background: #f8f9fa;
          min-height: 100vh;
          max-width: 480px;
          margin: 0 auto;
          position: relative;
        }

        .mobile-status-bar {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          position: sticky;
          top: 0;
          z-index: 1000;
        }

        .mobile-hero-section {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          margin-bottom: 2rem;
        }

        .mobile-content {
          min-height: calc(100vh - 120px);
          background: #f8f9fa;
        }

        .stat-card {
          background: white;
          border-radius: 16px;
          padding: 1.25rem;
          text-align: center;
          border: 1px solid #e9ecef;
        }

        .stat-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 0.75rem;
        }

        .stat-value {
          font-size: 1.75rem;
          font-weight: 700;
          margin-bottom: 0.25rem;
          color: #212529;
        }

        .stat-label {
          font-size: 0.75rem;
          color: #6c757d;
          font-weight: 500;
        }

        .mobile-bottom-nav {
          position: fixed;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          max-width: 480px;
          width: 100%;
          background: white;
          border-top: 1px solid #e9ecef;
          display: flex;
          justify-content: space-around;
          padding: 0.5rem 0;
          box-shadow: 0 -2px 10px rgba(0,0,0,0.05);
          z-index: 1000;
        }

        .nav-btn {
          flex: 1;
          background: none;
          border: none;
          padding: 0.5rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          color: #6c757d;
          transition: all 0.2s;
          gap: 0.25rem;
        }

        .nav-btn span {
          font-size: 0.7rem;
          font-weight: 500;
        }

        .nav-btn.active {
          color: #667eea;
        }

        .nav-btn:active {
          transform: scale(0.95);
        }

        .timeline-item {
          transition: transform 0.2s;
        }

        .timeline-item:active {
          transform: scale(0.98);
        }

        @media (max-width: 480px) {
          .stat-value {
            font-size: 1.5rem;
          }
          .stat-card {
            padding: 1rem;
          }
        }
      `}</style>
    </div >
  );
};

export default ParentDashboard;